// src/components/dashboards/DeanDashboard.jsx
// Light theme — white background, crisp blue-gray palette
import React, { useState, useEffect, useCallback, useRef } from 'react';
import api, { hostelInventoryAPI } from '../../services/api';
import HostelUserPortalTab from '../hostel/HostelUserPortalTab';
import Calendar from '../shared/Calendar';
import { notify, confirmDialog } from '../../utils/notify';

// ─── Global CSS ───────────────────────────────────────────────────────────────
const GLOBAL_CSS = `
  @import url('https://fonts.googleapis.com/css2?family=DM+Serif+Display:ital@0;1&family=DM+Sans:ital,opsz,wght@0,9..40,300;0,9..40,400;0,9..40,500;0,9..40,600;0,9..40,700;1,9..40,400&display=swap');
  *, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }
  :root {
    --bg:       #f0f3fa;
    --surface:  #ffffff;
    --surface2: #f5f7fd;
    --surface3: #eaeefc;
    --border:   #dde2f0;
    --border2:  #e8ecf8;

    --text:  #181d35;
    --text2: #4a5278;
    --text3: #9ba3c4;

    --green:     #1a9e66;  --green-bg:  rgba(26,158,102,.09);
    --red:       #d63060;  --red-bg:    rgba(214,48,96,.09);
    --yellow:    #c88a00;  --yellow-bg: rgba(200,138,0,.1);
    --orange:    #c96820;  --orange-bg: rgba(201,104,32,.09);
    --teal:      #0d9eb0;  --teal-bg:   rgba(13,158,176,.09);
    --purple:    #7b5cf0;  --purple-bg: rgba(123,92,240,.1);
    --accent:    #3d5ef5;  --accent-dim: rgba(61,94,245,.1);
    --indigo-bg: rgba(61,94,245,.08);
    --sidebar-w: 248px;
    --font-head: 'DM Serif Display', Georgia, serif;
    --font-body: 'DM Sans', system-ui, sans-serif;
    --radius:    10px;
    --radius-lg: 14px;
    --shadow-sm: 0 1px 4px rgba(60,80,160,.07), 0 0 0 1px rgba(60,80,160,.04);
    --shadow:    0 4px 16px rgba(60,80,160,.1);
    --shadow-lg: 0 16px 48px rgba(30,50,140,.14);
    --glow:      0 0 20px rgba(61,94,245,.12);
  }
  body { background: var(--bg); color: var(--text); font-family: var(--font-body); }
  ::-webkit-scrollbar { width: 4px; height: 4px; }
  ::-webkit-scrollbar-track { background: transparent; }
  ::-webkit-scrollbar-thumb { background: var(--border); border-radius: 4px; }
  @keyframes fadeUp  { from { opacity:0; transform:translateY(10px); } to { opacity:1; transform:translateY(0); } }
  @keyframes spin    { to { transform: rotate(360deg); } }
  @keyframes shimmer { 0%,100%{opacity:.15} 50%{opacity:.35} }
  @keyframes glow    { 0%,100%{box-shadow:0 0 8px rgba(61,94,245,.12)} 50%{box-shadow:0 0 20px rgba(61,94,245,.28)} }
  .d-anim  { animation: fadeUp .3s ease both; }
  .d-anim1 { animation-delay: .04s; } .d-anim2 { animation-delay: .09s; }
  .d-anim3 { animation-delay: .14s; } .d-anim4 { animation-delay: .19s; }
  .d-layout  { display: flex; min-height: 100vh; background: var(--bg); font-family: var(--font-body); }
  .d-sidebar { width: var(--sidebar-w); flex-shrink: 0; background: var(--surface); border-right: 1px solid var(--border); display: flex; flex-direction: column; position: fixed; top: 0; left: 0; height: 100vh; overflow-y: auto; z-index: 100; transition: transform .25s ease; }
  .d-main    { margin-left: var(--sidebar-w); flex: 1; display: flex; flex-direction: column; min-width: 0; }
  .d-topbar  { position: sticky; top: 0; z-index: 50; background: rgba(255,255,255,.94); backdrop-filter: blur(14px); border-bottom: 1px solid var(--border); padding: 0 28px; height: 64px; display: flex; align-items: center; justify-content: space-between; gap: 12px; }
  .d-content { flex: 1; padding: 28px; overflow-x: hidden; }
  .d-sg4 { display: grid; grid-template-columns: repeat(4,1fr); gap: 14px; margin-bottom: 16px; }
  .d-sg3 { display: grid; grid-template-columns: repeat(3,1fr); gap: 14px; margin-bottom: 16px; }
  .d-dg3 { display: grid; grid-template-columns: repeat(3,1fr); gap: 14px; }
  .d-2c  { display: grid; grid-template-columns: 1fr 1fr; gap: 14px; }
  .d-hbg { display: none; background: none; border: none; cursor: pointer; padding: 6px; color: var(--text2); align-items: center; }
  .d-ovl { display: none; position: fixed; inset: 0; background: rgba(20,30,80,.4); z-index: 99; }
  @media (max-width:1200px) { .d-sg4{grid-template-columns:repeat(2,1fr)} .d-dg3{grid-template-columns:repeat(2,1fr)} }
  @media (max-width:900px)  { .d-sg3{grid-template-columns:repeat(2,1fr)} }
  @media (max-width:768px)  {
    :root { --sidebar-w: 248px; }
    .d-sidebar { transform: translateX(-100%); }
    .d-sidebar.open { transform: translateX(0); box-shadow: var(--shadow-lg); }
    .d-ovl.open { display: block; }
    .d-hbg { display: flex !important; }
    .d-main { margin-left: 0; }
    .d-content { padding: 16px; }
    .d-sg4,.d-sg3 { grid-template-columns: 1fr 1fr; gap: 10px; }
    .d-dg3,.d-2c  { grid-template-columns: 1fr; }
  }
  @media (max-width:480px) { .d-sg4 { grid-template-columns: 1fr 1fr; gap: 8px; } .d-content { padding: 12px; } }
  .d-tw  { overflow-x: auto; -webkit-overflow-scrolling: touch; }
  .d-nav-item:hover { background: var(--accent-dim) !important; color: var(--accent) !important; }
  .d-btn:hover  { opacity: .82; }
  .d-btn:active { transform: scale(.97); }
  .d-tile:hover { box-shadow: var(--shadow), var(--glow) !important; transform: translateY(-1px); }
  .d-inp:focus  { border-color: var(--accent) !important; outline: none; box-shadow: 0 0 0 3px var(--accent-dim); }
  .d-sel:focus  { border-color: var(--accent) !important; outline: none; }
  .d-tag { display: inline-flex; align-items: center; gap: 4px; padding: 2px 8px; border-radius: 20px; font-size: 11px; font-weight: 600; letter-spacing: .02em; white-space: nowrap; }
`;

// ─── Helpers ──────────────────────────────────────────────────────────────────
const fv    = (v, fb = '—') => (v === null || v === undefined || v === '') ? fb : v;
const fmtD  = d => d ? new Date(d).toLocaleDateString('en-IN', { day:'2-digit', month:'short', year:'numeric' }) : '—';
const fmtC  = v => v != null ? `₹${parseFloat(v).toLocaleString('en-IN', { minimumFractionDigits:2, maximumFractionDigits:2 })}` : '—';
const fmtK  = v => {
  if (v == null || isNaN(parseFloat(v))) return '—';
  const n = parseFloat(v);
  if (n >= 1e7) return `₹${(n/1e7).toFixed(2)}Cr`;
  if (n >= 1e5) return `₹${(n/1e5).toFixed(1)}L`;
  if (n >= 1e3) return `₹${(n/1e3).toFixed(1)}K`;
  return fmtC(v);
};
const ini   = u => `${u.first_name?.[0] || ''}${u.last_name?.[0] || u.username?.[0] || '?'}`.toUpperCase();
const toArr = d => Array.isArray(d) ? d : (d?.results || []);

// ─── Role / Status metadata ───────────────────────────────────────────────────
const ROLE_META = {
  ADMIN:                 { label:'Admin',          c:'#7b5cf0', bg:'rgba(123,92,240,.1)'   },
  DEAN:                  { label:'Dean',            c:'#3d5ef5', bg:'rgba(61,94,245,.1)'   },
  PRINCIPAL:             { label:'Principal',       c:'#3d5ef5', bg:'rgba(61,94,245,.08)'  },
  STUDENT:               { label:'Student',         c:'#3d5ef5', bg:'rgba(61,94,245,.08)'  },
  FACULTY:               { label:'Faculty',         c:'#1a9e66', bg:'rgba(26,158,102,.09)' },
  FINANCE:               { label:'Finance',         c:'#c88a00', bg:'rgba(200,138,0,.1)'   },
  HOSTEL_ADMIN:          { label:'Hostel',          c:'#c96820', bg:'rgba(201,104,32,.09)' },
  LIBRARY_STAFF:         { label:'Library',         c:'#7b5cf0', bg:'rgba(123,92,240,.1)'  },
  TRANSPORT_MANAGER:     { label:'Transport',       c:'#0d9eb0', bg:'rgba(13,158,176,.09)' },
  recepsonist:           { label:'Reception',       c:'#1a9e66', bg:'rgba(26,158,102,.09)' },
  LAB_TECH_MICROBIOLOGY: { label:'Lab–Micro',       c:'#d63060', bg:'rgba(214,48,96,.09)'  },
  LAB_TECH_PATHOLOGY:    { label:'Lab–Patho',       c:'#d63060', bg:'rgba(214,48,96,.09)'  },
  LAB_TECH_BIOCHEMISTRY: { label:'Lab–Biochem',     c:'#c96820', bg:'rgba(201,104,32,.09)' },
  BILLING_MEMBER:        { label:'Billing',         c:'#c88a00', bg:'rgba(200,138,0,.1)'   },
  BILLING_MANAGER:       { label:'Billing Mgr',     c:'#c88a00', bg:'rgba(200,138,0,.1)'   },
  SAMPLE_COLLECTOR:      { label:'Sample Col.',     c:'#3d5ef5', bg:'rgba(61,94,245,.08)'  },
  REPORT_GENERATION:     { label:'Reports',         c:'#6b7494', bg:'rgba(107,116,148,.1)' },
  GRIEVANCE_OFFICER:     { label:'Grievance Off.',  c:'#7b5cf0', bg:'rgba(123,92,240,.1)'  },
};

const GS_COLOR = {
  PENDING:      { c:'#c88a00', bg:'rgba(200,138,0,.1)'   },
  ASSIGNED:     { c:'#3d5ef5', bg:'rgba(61,94,245,.08)'  },
  UNDER_REVIEW: { c:'#c96820', bg:'rgba(201,104,32,.09)' },
  RESOLVED:     { c:'#1a9e66', bg:'rgba(26,158,102,.09)' },
  REJECTED:     { c:'#d63060', bg:'rgba(214,48,96,.09)'  },
  ESCALATED:    { c:'#d63060', bg:'rgba(214,48,96,.12)'  },
  CLOSED:       { c:'#6b7494', bg:'rgba(107,116,148,.1)' },
  REOPENED:     { c:'#c96820', bg:'rgba(201,104,32,.09)' },
};

// ─── Primitive components ─────────────────────────────────────────────────────
const Tag = ({ label, color, bg }) => (
  <span className="d-tag" style={{ color, background: bg, border: `1px solid ${color}30` }}>{label}</span>
);
const RoleTag = ({ role }) => {
  const m = ROLE_META[role] || { label: role, c:'#6b7494', bg:'rgba(107,116,148,.1)' };
  return <Tag label={m.label} color={m.c} bg={m.bg} />;
};
const StatusTag = ({ status }) => {
  const MAP = {
    APPROVED: { c:'#1a9e66', bg:'rgba(26,158,102,.09)' },
    PENDING:  { c:'#c88a00', bg:'rgba(200,138,0,.1)'   },
    REJECTED: { c:'#d63060', bg:'rgba(214,48,96,.09)'  },
  };
  const m = MAP[status] || { c:'#6b7494', bg:'rgba(107,116,148,.1)' };
  return <Tag label={status} color={m.c} bg={m.bg} />;
};

const Spin = () => (
  <div style={{ display:'flex', justifyContent:'center', padding:'56px 0' }}>
    <div style={{ width:28, height:28, border:'2px solid var(--border)', borderTop:'2px solid var(--accent)', borderRadius:'50%', animation:'spin .7s linear infinite' }} />
  </div>
);
const Empty = ({ msg = 'No records found' }) => (
  <div style={{ textAlign:'center', padding:'56px 20px', color:'var(--text3)' }}>
    <div style={{ fontSize:36, marginBottom:10, opacity:.25 }}>∅</div>
    <p style={{ fontSize:13 }}>{msg}</p>
  </div>
);
const Card = ({ children, style }) => (
  <div style={{ background:'var(--surface)', borderRadius:'var(--radius-lg)', border:'1px solid var(--border)', boxShadow:'var(--shadow-sm)', ...style }}>
    {children}
  </div>
);
const CardHdr = ({ title, sub, right }) => (
  <div style={{ padding:'14px 20px', borderBottom:'1px solid var(--border2)', display:'flex', alignItems:'center', justifyContent:'space-between', gap:12, flexWrap:'wrap' }}>
    <div>
      <div style={{ fontSize:13, fontWeight:700, color:'var(--text)', letterSpacing:'.01em' }}>{title}</div>
      {sub && <div style={{ fontSize:11, color:'var(--text3)', marginTop:2 }}>{sub}</div>}
    </div>
    {right && <div style={{ display:'flex', gap:6, flexWrap:'wrap', alignItems:'center' }}>{right}</div>}
  </div>
);
const Btn = ({ children, onClick, v = 'primary', xs, disabled, style }) => {
  const sz = xs ? { padding:'4px 12px', fontSize:11 } : { padding:'8px 16px', fontSize:12.5 };
  const V = {
    primary: { background:'var(--accent)',      color:'#fff',          border:'none',                           boxShadow:'0 2px 8px rgba(61,94,245,.25)' },
    ghost:   { background:'var(--surface2)',    color:'var(--text2)',  border:'1px solid var(--border)',        boxShadow:'none' },
    danger:  { background:'var(--red-bg)',      color:'var(--red)',    border:'1px solid rgba(214,48,96,.25)',  boxShadow:'none' },
    success: { background:'var(--green-bg)',    color:'var(--green)',  border:'1px solid rgba(26,158,102,.25)', boxShadow:'none' },
    outline: { background:'transparent',        color:'var(--text2)',  border:'1px solid var(--border)',        boxShadow:'none' },
    accent:  { background:'var(--indigo-bg)',   color:'var(--accent)', border:'1px solid rgba(61,94,245,.2)',   boxShadow:'none' },
  };
  return (
    <button className="d-btn" onClick={onClick} disabled={disabled} style={{
      ...sz, ...V[v], borderRadius:8, cursor: disabled ? 'not-allowed' : 'pointer',
      fontWeight:600, fontFamily:'var(--font-body)', opacity: disabled ? .4 : 1,
      whiteSpace:'nowrap', transition:'opacity .15s, transform .1s', ...style,
    }}>{children}</button>
  );
};
const Inp = ({ value, onChange, placeholder, style }) => (
  <input className="d-inp" value={value} onChange={onChange} placeholder={placeholder}
    style={{ background:'var(--surface)', border:'1px solid var(--border)', borderRadius:8, padding:'7px 13px', color:'var(--text)', fontSize:13, fontFamily:'var(--font-body)', transition:'border-color .15s, box-shadow .15s', ...style }} />
);
const Sel = ({ value, onChange, children, style }) => (
  <select className="d-sel" value={value} onChange={onChange}
    style={{ background:'var(--surface)', border:'1px solid var(--border)', borderRadius:8, padding:'7px 11px', color:'var(--text)', fontSize:13, cursor:'pointer', fontFamily:'var(--font-body)', ...style }}>
    {children}
  </select>
);
const Fld = ({ label, value, span, mono, highlight, children }) => (
  <div style={{ background:'var(--surface2)', borderRadius:8, padding:'10px 14px', border:'1px solid var(--border2)', ...(span ? { gridColumn:'span 2' } : {}) }}>
    <div style={{ fontSize:10, fontWeight:700, letterSpacing:'.08em', textTransform:'uppercase', color:'var(--text3)', marginBottom:3 }}>{label}</div>
    {children
      ? <div style={{ fontSize:13, color:'var(--text)', fontWeight:500 }}>{children}</div>
      : <div style={{ fontSize:13, color: highlight ? 'var(--accent)' : 'var(--text)', fontWeight:500, fontFamily: mono ? 'monospace' : 'var(--font-body)' }}>{fv(value)}</div>
    }
  </div>
);
const SecHd = ({ label }) => (
  <div style={{ fontSize:10, fontWeight:700, letterSpacing:'.12em', textTransform:'uppercase', color:'var(--accent)', marginBottom:10, marginTop:18, paddingBottom:6, borderBottom:'1px solid var(--accent-dim)' }}>{label}</div>
);
const Av = ({ user, size = 34 }) => {
  const m = ROLE_META[user.role] || { c:'#6b7494', bg:'rgba(107,116,148,.1)' };
  return (
    <div style={{ width:size, height:size, borderRadius:'50%', flexShrink:0, background:m.bg, border:`1.5px solid ${m.c}44`, display:'flex', alignItems:'center', justifyContent:'center', fontSize:Math.floor(size*.38), fontWeight:700, color:m.c, overflow:'hidden' }}>
      {user.profile_picture ? <img src={user.profile_picture} alt="" style={{ width:'100%', height:'100%', objectFit:'cover' }} /> : ini(user)}
    </div>
  );
};
const Td = ({ children, style }) => (
  <td style={{ padding:'10px 16px', fontSize:13, color:'var(--text2)', borderBottom:'1px solid var(--border2)', verticalAlign:'middle', ...style }}>{children}</td>
);
const THead = ({ cols }) => (
  <thead>
    <tr>{cols.map(c => (
      <th key={c} style={{ padding:'9px 16px', textAlign:'left', fontSize:10, fontWeight:700, letterSpacing:'.1em', textTransform:'uppercase', color:'var(--text3)', borderBottom:'1px solid var(--border)', background:'var(--surface2)', whiteSpace:'nowrap' }}>{c}</th>
    ))}</tr>
  </thead>
);
const TblWrap = ({ loading, empty, cols, rows }) => {
  if (loading) return <Spin />;
  if (!rows || rows.length === 0) return <Empty msg={empty} />;
  return (
    <div className="d-tw">
      <table style={{ width:'100%', borderCollapse:'collapse', minWidth: cols.length * 100 }}>
        <THead cols={cols} />
        <tbody>{rows}</tbody>
      </table>
    </div>
  );
};
const Pager = ({ page, total, pageSize, setPage }) => {
  const tp = Math.ceil(total / pageSize);
  if (tp <= 1) return null;
  const lo = Math.max(1, page - 2), hi = Math.min(tp, page + 2);
  const pages = []; for (let i = lo; i <= hi; i++) pages.push(i);
  return (
    <div style={{ display:'flex', alignItems:'center', gap:5, justifyContent:'center', padding:'12px 16px', borderTop:'1px solid var(--border2)', flexWrap:'wrap' }}>
      <Btn v="outline" xs disabled={page===1}  onClick={() => setPage(1)}>«</Btn>
      <Btn v="outline" xs disabled={page===1}  onClick={() => setPage(p => p-1)}>‹</Btn>
      {pages.map(p2 => <Btn key={p2} xs v={p2===page?'primary':'outline'} onClick={() => setPage(p2)}>{p2}</Btn>)}
      <Btn v="outline" xs disabled={page===tp} onClick={() => setPage(p => p+1)}>›</Btn>
      <Btn v="outline" xs disabled={page===tp} onClick={() => setPage(tp)}>»</Btn>
      <span style={{ fontSize:11, color:'var(--text3)', marginLeft:6 }}>{total.toLocaleString()} total · {page}/{tp}</span>
    </div>
  );
};
const Tile = ({ icon, label, value, color, colorBg, sub, onClick }) => (
  <div className="d-tile d-anim" onClick={onClick} style={{
    background:'var(--surface)', borderRadius:'var(--radius-lg)', border:'1px solid var(--border)',
    borderTop:`3px solid ${color}`, padding:'18px 20px', cursor: onClick ? 'pointer' : 'default',
    transition:'box-shadow .2s, transform .15s', boxShadow:'var(--shadow-sm)',
  }}>
    <div style={{ display:'flex', alignItems:'flex-start', justifyContent:'space-between', marginBottom:12 }}>
      <div style={{ width:42, height:42, borderRadius:12, background:colorBg, display:'flex', alignItems:'center', justifyContent:'center', fontSize:20 }}>{icon}</div>
      {onClick && <span style={{ fontSize:16, color:'var(--text3)' }}>›</span>}
    </div>
    <div style={{ fontSize:28, fontWeight:400, fontFamily:'var(--font-head)', color:'var(--text)', letterSpacing:'-.02em', lineHeight:1 }}>
      {value != null ? value : <span style={{ display:'inline-block', width:56, height:28, background:'var(--surface3)', borderRadius:4, animation:'shimmer 1.4s ease infinite' }} />}
    </div>
    <div style={{ fontSize:12, color:'var(--text2)', marginTop:5, fontWeight:500 }}>{label}</div>
    {sub && <div style={{ fontSize:11, color, marginTop:3, fontWeight:600 }}>{sub}</div>}
  </div>
);

// ─── User Detail Modal ────────────────────────────────────────────────────────
function UserModal({ userId, onClose }) {
  const [adminUser, setAdminUser] = useState(null);
  const [finUser,   setFinUser]   = useState(null);
  const [loading,   setLoading]   = useState(true);
  const [tab,       setTab]       = useState('identity');

  const TABS = [
    { id:'identity', label:'🪪 Identity'       },
    { id:'profile',  label:'👤 Profile'         },
    { id:'academic', label:'📚 Academic / Work' },
    { id:'finance',  label:'💰 Financial'       },
    { id:'bank',     label:'🏦 Bank'            },
    { id:'timeline', label:'📋 Timeline'        },
  ];

  useEffect(() => {
    let active = true;
    setLoading(true);
    Promise.allSettled([
      api.get(`/auth/admin/users/${userId}/`).then(r => r.data),
      api.get('/auth/finance/users/', { params: { page_size: 2000 } }).then(r => {
        const list = toArr(r.data);
        return list.find(u => u.id === parseInt(userId)) || null;
      }),
    ]).then(([aRes, fRes]) => {
      if (!active) return;
      setAdminUser(aRes.status === 'fulfilled' ? aRes.value : null);
      setFinUser  (fRes.status === 'fulfilled' ? fRes.value : null);
      setLoading(false);
    });
    return () => { active = false; };
  }, [userId]);

  const u       = adminUser || {};
  const fu      = finUser   || {};
  const profile = { ...(fu.profile || {}), ...(u.profile || {}) };
  const fr      = fu.financial_record || {};
  const bd      = fu.bank_detail      || {};
  const role    = u.role || fu.role || '';
  const m       = ROLE_META[role] || { c:'#6b7494', bg:'rgba(107,116,148,.1)' };
  const fullName = `${u.first_name || fu.first_name || ''} ${u.last_name || fu.last_name || ''}`.trim() || u.username || fu.username || '';

  return (
    <div style={{ position:'fixed', inset:0, background:'rgba(20,30,80,.5)', display:'flex', alignItems:'center', justifyContent:'center', zIndex:300, backdropFilter:'blur(6px)', padding:16 }} onClick={onClose}>
      <div style={{ background:'var(--surface)', borderRadius:18, border:'1px solid var(--border)', width:'100%', maxWidth:860, maxHeight:'92vh', display:'flex', flexDirection:'column', overflow:'hidden', boxShadow:'var(--shadow-lg)' }} onClick={e => e.stopPropagation()}>

        {loading && <div style={{ padding:80 }}><Spin /></div>}

        {!loading && !adminUser && (
          <div style={{ padding:40 }}>
            <Empty msg="Could not load user. Grant Dean access to /auth/admin/users/." />
            <div style={{ textAlign:'center', marginTop:12 }}><Btn v="ghost" onClick={onClose}>Close</Btn></div>
          </div>
        )}

        {!loading && adminUser && (<>
          {/* Header */}
          <div style={{ padding:'20px 24px', borderBottom:'1px solid var(--border)', display:'flex', alignItems:'center', justifyContent:'space-between', background:'var(--surface2)', gap:16, flexWrap:'wrap' }}>
            <div style={{ display:'flex', alignItems:'center', gap:16 }}>
              <div style={{ width:60, height:60, borderRadius:'50%', background:m.bg, border:`2px solid ${m.c}44`, display:'flex', alignItems:'center', justifyContent:'center', fontSize:22, fontWeight:700, color:m.c, overflow:'hidden', flexShrink:0 }}>
                {u.profile_picture ? <img src={u.profile_picture} alt="" style={{ width:'100%', height:'100%', objectFit:'cover' }} /> : ini(u)}
              </div>
              <div>
                <div style={{ fontFamily:'var(--font-head)', fontSize:20, color:'var(--text)', fontWeight:400, lineHeight:1.2 }}>{fullName}</div>
                <div style={{ fontSize:12, color:'var(--text3)', marginTop:3 }}>@{u.username} · {u.email}</div>
                <div style={{ display:'flex', gap:6, marginTop:7, flexWrap:'wrap' }}>
                  <RoleTag role={role} />
                  <StatusTag status={u.approval_status} />
                  {u.is_verified && <Tag label="✓ Verified"  color="#1a9e66" bg="rgba(26,158,102,.09)" />}
                  {u.is_active === false && <Tag label="Inactive" color="#d63060" bg="rgba(214,48,96,.09)" />}
                  {profile.faculty_subrole && <Tag label={profile.faculty_subrole.replace(/_/g,' ')} color="#1a9e66" bg="rgba(26,158,102,.09)" />}
                </div>
              </div>
            </div>
            <button onClick={onClose} style={{ background:'none', border:'none', cursor:'pointer', color:'var(--text3)', fontSize:22, padding:6, lineHeight:1 }}>✕</button>
          </div>

          {/* Tabs */}
          <div style={{ display:'flex', borderBottom:'1px solid var(--border)', background:'var(--surface)', paddingLeft:20, overflowX:'auto' }}>
            {TABS.map(t => (
              <button key={t.id} onClick={() => setTab(t.id)} style={{ padding:'10px 16px', border:'none', cursor:'pointer', background:'none', fontSize:12.5, fontWeight: tab===t.id ? 700 : 400, color: tab===t.id ? 'var(--accent)' : 'var(--text3)', borderBottom: tab===t.id ? '2px solid var(--accent)' : '2px solid transparent', transition:'all .15s', fontFamily:'var(--font-body)', whiteSpace:'nowrap' }}>{t.label}</button>
            ))}
          </div>

          {/* Tab body */}
          <div style={{ padding:'20px 24px', overflowY:'auto', flex:1, background:'var(--surface)' }}>

            {tab === 'identity' && (<>
              <SecHd label="Account Information" />
              {!finUser && (
                <div style={{ background:'rgba(200,138,0,.07)', borderRadius:8, border:'1px solid rgba(200,138,0,.25)', padding:'10px 14px', marginBottom:12, fontSize:12, color:'var(--yellow)' }}>
                  ⚠ Financial data unavailable — grant Dean access to <code>/auth/finance/users/</code> for salary, fee & bank details.
                </div>
              )}
              <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fill, minmax(210px,1fr))', gap:10 }}>
                <Fld label="Full Name"       value={fullName} />
                <Fld label="Username"        value={u.username} mono />
                <Fld label="Email"           value={u.email} />
                <Fld label="Phone"           value={u.phone_number} />
                <Fld label="Role"            value={(ROLE_META[role] || {}).label || role} />
                <Fld label="Approval Status"><StatusTag status={u.approval_status} /></Fld>
                <Fld label="Account Status"  value={u.is_active === false ? 'Inactive' : 'Active'} />
                <Fld label="Email Verified"  value={u.is_verified ? 'Yes' : 'No'} />
                <Fld label="Registered On"   value={fmtD(u.created_at)} />
                <Fld label="Approved By"     value={u.approved_by_name || '—'} />
                <Fld label="Approved On"     value={fmtD(u.approved_at)} />
                {u.rejection_reason && <Fld label="Rejection Reason" value={u.rejection_reason} span />}
              </div>
            </>)}

            {tab === 'profile' && (<>
              <SecHd label="Personal Information" />
              <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fill, minmax(210px,1fr))', gap:10 }}>
                <Fld label="Date of Birth"     value={fmtD(profile.date_of_birth)} />
                <Fld label="Gender"            value={{ M:'Male', F:'Female', O:'Other' }[profile.gender] || profile.gender} />
                <Fld label="Blood Group"       value={profile.blood_group} />
                <Fld label="Emergency Contact" value={profile.emergency_contact} />
                {profile.address && <Fld label="Address" value={profile.address} span />}
              </div>
            </>)}

            {tab === 'academic' && (<>
              {role === 'STUDENT' && (<>
                <SecHd label="Student Information" />
                <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fill, minmax(210px,1fr))', gap:10 }}>
                  <Fld label="Student ID"         value={profile.student_id}   mono highlight />
                  <Fld label="Course"             value={profile.course_display || profile.course} />
                  <Fld label="Enrollment Year"    value={profile.enrollment_year} />
                  <Fld label="Current Semester"   value={profile.current_semester} />
                  <Fld label="Department"         value={profile.student_department_name || 'General / MBBS'} />
                  <Fld label="Assigned Professor" value={profile.assigned_professor_name || '—'} />
                  <Fld label="Entrance Score"     value={profile.entrance_score} />
                </div>
              </>)}
              {role === 'FACULTY' && (<>
                <SecHd label="Faculty Information" />
                <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fill, minmax(210px,1fr))', gap:10 }}>
                  <Fld label="Employee ID"   value={profile.employee_id}                       mono highlight />
                  <Fld label="Department"    value={profile.faculty_department_name || '—'} />
                  <Fld label="Subrole"       value={profile.faculty_subrole?.replace(/_/g,' ')} />
                  <Fld label="Designation"   value={profile.designation} />
                  <Fld label="Joining Date"  value={fmtD(profile.joining_date)} />
                </div>
              </>)}
              {!['STUDENT','FACULTY'].includes(role) && (<>
                <SecHd label="Staff Information" />
                <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fill, minmax(210px,1fr))', gap:10 }}>
                  <Fld label="Employee ID"  value={profile.employee_id} mono highlight />
                  <Fld label="Designation"  value={profile.designation} />
                  <Fld label="Joining Date" value={fmtD(profile.joining_date)} />
                </div>
              </>)}
            </>)}

            {tab === 'finance' && (<>
              {role === 'STUDENT' ? (<>
                <SecHd label="Tuition Fee Details" />
                <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fill, minmax(210px,1fr))', gap:10 }}>
                  <Fld label="Total Fee"         value={fmtC(fr.tuition_fee)} />
                  <Fld label="Amount Paid"       value={fmtC(fr.tuition_paid)}    highlight />
                  <Fld label="Amount Pending"    value={fmtC(fr.tuition_pending)} />
                  <Fld label="Payment Deadline"  value={fmtD(fr.payment_deadline)} />
                  <Fld label="Last Payment Date" value={fmtD(fr.last_payment_date)} />
                  <Fld label="Overdue Status">
                    {fr.is_overdue
                      ? <Tag label="⚠ Overdue"  color="#d63060" bg="rgba(214,48,96,.09)" />
                      : <Tag label="✓ On Track" color="#1a9e66" bg="rgba(26,158,102,.09)" />}
                  </Fld>
                </div>
              </>) : (<>
                <SecHd label="Salary Details" />
                <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fill, minmax(210px,1fr))', gap:10 }}>
                  <Fld label="Monthly Salary"   value={fmtC(fr.monthly_salary)} highlight />
                  <Fld label="Last Salary Paid" value={fmtD(fr.last_salary_paid)} />
                </div>
              </>)}
              {!finUser && (
                <div style={{ background:'rgba(200,138,0,.07)', borderRadius:8, border:'1px solid rgba(200,138,0,.25)', padding:'10px 14px', marginTop:12, fontSize:12, color:'var(--yellow)' }}>
                  Financial data not loaded. Add <code>IsAdminOrDean</code> to <code>FinanceUserListView.permission_classes</code>.
                </div>
              )}
            </>)}

            {tab === 'bank' && (<>
              <SecHd label="Bank Account Details" />
              {!bd.bank_name
                ? <p style={{ color:'var(--text3)', fontSize:13 }}>No bank details on record.{!finUser && ' (Finance access required.)'}</p>
                : (
                  <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fill, minmax(210px,1fr))', gap:10 }}>
                    <Fld label="Account Holder" value={bd.account_holder_name} />
                    <Fld label="Bank Name"      value={bd.bank_name} />
                    <Fld label="Account No."    value={bd.account_number} mono />
                    <Fld label="IFSC Code"      value={bd.ifsc_code}     mono />
                    <Fld label="Branch"         value={bd.branch} />
                    <Fld label="Last Updated"   value={fmtD(bd.updated_at)} />
                  </div>
                )}
            </>)}

            {tab === 'timeline' && (<>
              <SecHd label="Account Timeline" />
              <div style={{ display:'flex', flexDirection:'column', gap:8 }}>
                {[
                  { icon:'🆕', label:'Registered',        value: fmtD(u.created_at) },
                  u.approved_at       && { icon:'✅', label:'Approved On',      value: fmtD(u.approved_at) },
                  u.approved_by_name  && { icon:'👤', label:'Approved By',      value: u.approved_by_name },
                  profile.joining_date && { icon:'🏢', label:'Joined',           value: fmtD(profile.joining_date) },
                  fr.last_payment_date && { icon:'💳', label:'Last Payment',     value: fmtD(fr.last_payment_date) },
                  fr.last_salary_paid  && { icon:'💰', label:'Last Salary',      value: fmtD(fr.last_salary_paid) },
                  fr.payment_deadline  && { icon:'⏰', label:'Fee Deadline',     value: fmtD(fr.payment_deadline) },
                ].filter(Boolean).map((ev, i) => (
                  <div key={i} style={{ display:'flex', alignItems:'center', gap:12, background:'var(--surface2)', borderRadius:8, padding:'10px 14px', border:'1px solid var(--border2)' }}>
                    <span style={{ fontSize:16 }}>{ev.icon}</span>
                    <div style={{ flex:1, fontSize:13, color:'var(--text2)' }}>{ev.label}</div>
                    <div style={{ fontSize:13, color:'var(--accent)', fontWeight:600 }}>{ev.value}</div>
                  </div>
                ))}
              </div>
            </>)}
          </div>
        </>)}
      </div>
    </div>
  );
}

// ─── Grievance Modal ──────────────────────────────────────────────────────────
function GrievanceModal({ id, onClose, onUpdate }) {
  const [data, setData]   = useState(null);
  const [loading, setLd]  = useState(true);
  const [note, setNote]   = useState('');
  const [newSt, setNewSt] = useState('');
  const [busy, setBusy]   = useState(false);

  const reload = useCallback(() => {
    setLd(true);
    api.get(`/auth/grievances/${id}/`).then(r => setData(r.data)).catch(console.error).finally(() => setLd(false));
  }, [id]);
  useEffect(() => { reload(); }, [reload]);

  const canUpdate = ['ESCALATED','UNDER_REVIEW','REOPENED'].includes(data?.status);
  const validNext = { ESCALATED:['UNDER_REVIEW','RESOLVED','REJECTED'], UNDER_REVIEW:['RESOLVED','REJECTED'], REOPENED:['UNDER_REVIEW','RESOLVED'] }[data?.status] || [];

  const doStatus = async () => {
    if (!newSt) return; setBusy(true);
    try {
      await api.post(`/auth/grievances/${id}/update-status/`, { status:newSt, note:note||undefined, resolution_notes:newSt==='RESOLVED'?note:undefined, rejection_reason:newSt==='REJECTED'?note:undefined });
      reload(); onUpdate(); setNote(''); setNewSt('');
    } catch(e) { notify.error('Error: ' + (e.response?.data?.detail || e.message)); }
    finally { setBusy(false); }
  };
  const doRespond = async () => {
    if (!note.trim()) return; setBusy(true);
    const fd = new FormData(); fd.append('message', note); fd.append('response_type','DEPARTMENT_ACTION');
    try { await api.post(`/auth/grievances/${id}/respond/`, fd, { headers:{'Content-Type':'multipart/form-data'} }); reload(); setNote(''); }
    catch(e) { notify.error('Error: ' + (e.response?.data?.detail || e.message)); }
    finally { setBusy(false); }
  };

  const gs = data ? GS_COLOR[data.status] || GS_COLOR.CLOSED : null;
  return (
    <div style={{ position:'fixed', inset:0, background:'rgba(20,30,80,.5)', display:'flex', alignItems:'center', justifyContent:'center', zIndex:300, backdropFilter:'blur(6px)', padding:16 }} onClick={onClose}>
      <div style={{ background:'var(--surface)', borderRadius:18, border:'1px solid var(--border)', width:'100%', maxWidth:720, maxHeight:'90vh', display:'flex', flexDirection:'column', overflow:'hidden', boxShadow:'var(--shadow-lg)' }} onClick={e => e.stopPropagation()}>
        <div style={{ padding:'18px 22px', borderBottom:'1px solid var(--border)', display:'flex', alignItems:'center', justifyContent:'space-between', background:'var(--surface2)' }}>
          <div>
            <div style={{ fontFamily:'var(--font-head)', fontSize:16, color:'var(--text)', fontWeight:400 }}>{loading ? 'Loading…' : data?.grievance_id || 'Grievance'}</div>
            {data && gs && (
              <div style={{ display:'flex', gap:6, marginTop:6, flexWrap:'wrap' }}>
                <Tag label={data.status}   color={gs.c} bg={gs.bg} />
                <Tag label={data.priority} color={data.priority==='HIGH'?'#d63060':data.priority==='MEDIUM'?'#c88a00':'#6b7494'} bg={data.priority==='HIGH'?'rgba(214,48,96,.09)':data.priority==='MEDIUM'?'rgba(200,138,0,.1)':'rgba(107,116,148,.1)'} />
                {data.escalated_to === 'DEAN' && <Tag label="Escalated to Dean" color="#3d5ef5" bg="rgba(61,94,245,.1)" />}
              </div>
            )}
          </div>
          <button onClick={onClose} style={{ background:'none', border:'none', cursor:'pointer', color:'var(--text3)', fontSize:22 }}>✕</button>
        </div>

        {loading ? <div style={{ padding:60 }}><Spin /></div> : !data ? <Empty /> : (
          <div style={{ overflowY:'auto', flex:1, padding:'20px 22px' }}>
            <SecHd label="Grievance Details" />
            <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fill, minmax(180px,1fr))', gap:10 }}>
              <Fld label="Subject"      value={data.subject} span />
              <Fld label="Submitted By" value={`${data.submitted_by?.first_name||''} ${data.submitted_by?.last_name||data.submitted_by?.username||''}`.trim()} />
              <Fld label="Category"     value={data.category_display} />
              <Fld label="Date"         value={fmtD(data.created_at)} />
              <Fld label="Department"   value={data.department_assigned || '—'} />
              <Fld label="Assigned To"  value={data.assigned_to ? `${data.assigned_to.first_name||''} ${data.assigned_to.last_name||data.assigned_to.username}`.trim() : '—'} />
            </div>
            <SecHd label="Description" />
            <div style={{ background:'var(--surface2)', borderRadius:8, padding:'14px', border:'1px solid var(--border2)', fontSize:13, color:'var(--text)', lineHeight:1.7 }}>{data.description}</div>
            {data.escalation_reason && (<>
              <SecHd label="Escalation Reason" />
              <div style={{ background:'rgba(200,138,0,.07)', borderRadius:8, padding:'14px', border:'1px solid rgba(200,138,0,.2)', fontSize:13, color:'var(--text)', lineHeight:1.7 }}>{data.escalation_reason}</div>
            </>)}
            {data.responses?.length > 0 && (<>
              <SecHd label={`Responses (${data.responses.length})`} />
              {data.responses.map(r => (
                <div key={r.id} style={{ background:'var(--surface2)', borderRadius:8, padding:'12px 14px', border:'1px solid var(--border2)', marginBottom:8 }}>
                  <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:5 }}>
                    <span style={{ fontSize:12, fontWeight:600, color:'var(--accent)' }}>{r.responded_by?.first_name||''} {r.responded_by?.last_name||r.responded_by?.username}</span>
                    <span style={{ fontSize:11, color:'var(--text3)' }}>{fmtD(r.created_at)}</span>
                  </div>
                  <p style={{ fontSize:13, color:'var(--text2)', lineHeight:1.6 }}>{r.message}</p>
                </div>
              ))}
            </>)}
            <SecHd label="Dean Actions" />
            <div style={{ background:'var(--surface2)', borderRadius:10, padding:'16px', border:'1px solid var(--border)' }}>
              <textarea value={note} onChange={e => setNote(e.target.value)} placeholder="Add resolution notes, rejection reason, or a response…" rows={3}
                style={{ width:'100%', background:'var(--surface)', border:'1px solid var(--border)', borderRadius:8, padding:'10px 13px', color:'var(--text)', fontSize:13, outline:'none', resize:'vertical', fontFamily:'var(--font-body)', lineHeight:1.5 }} />
              <div style={{ display:'flex', gap:8, marginTop:10, flexWrap:'wrap' }}>
                {canUpdate && (<>
                  <Sel value={newSt} onChange={e => setNewSt(e.target.value)} style={{ flex:'1 1 160px' }}>
                    <option value="">— Change Status —</option>
                    {validNext.map(s => <option key={s} value={s}>{s}</option>)}
                  </Sel>
                  <Btn onClick={doStatus} disabled={!newSt || busy}>{busy ? 'Saving…' : 'Update Status'}</Btn>
                </>)}
                <Btn v="ghost" onClick={doRespond} disabled={!note.trim() || busy}>{busy ? 'Sending…' : 'Add Response'}</Btn>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════════
// MAIN DASHBOARD COMPONENT
// ═══════════════════════════════════════════════════════════════════════════════
export default function DeanDashboard() {
  const [hasHostelAllocation, setHasHostelAllocation] = useState(false);

  useEffect(() => {
    hostelInventoryAPI.getMyHostelProfile()
      .then(res => {
        if (res && res.allocated) {
          setHasHostelAllocation(true);
        }
      })
      .catch(() => {});
  }, []);

  const [section,  setSection]  = useState('overview');
  const [sideOpen, setSideOpen] = useState(false);

  const [stats,       setStats]       = useState(null);
  const [finStats,    setFinStats]    = useState(null);
  const [feeStructs,  setFeeStructs]  = useState([]);
  const [salStructs,  setSalStructs]  = useState([]);
  const [labStats,    setLabStats]    = useState(null);
  const [courseBreak, setCourseBreak] = useState([]);
  const [depts,       setDepts]       = useState([]);
  const [grievStats,  setGrievStats]  = useState(null);
  const [leaveStats,  setLeaveStats]  = useState(null);
  const [paySummary,  setPaySummary]  = useState(null);
  const [opd,         setOpd]         = useState(null);

  const [users,      setUsers]      = useState([]); const [uTotal,  setUTotal]  = useState(0);
  const [leaves,     setLeaves]     = useState([]); const [lvTotal, setLvTotal] = useState(0);
  const [payroll,    setPayroll]    = useState([]); const [pyTotal, setPyTotal] = useState(0);
  const [grievances, setGrievances] = useState([]);

  const [allFaculty,  setAllFaculty]  = useState([]);
  const [allStudents, setAllStudents] = useState([]);

  const [loading, setLoading] = useState({});
  const [selUser,  setSelUser]  = useState(null);
  const [selGriev, setSelGriev] = useState(null);

  const [uSearch, setUSearch] = useState('');
  const [uRole,   setURole]   = useState('');
  const [uStatus, setUStatus] = useState('');
  const [uPage,   setUPage]   = useState(1);

  const [fSearch, setFSearch] = useState('');
  const [fDept,   setFDept]   = useState('');
  const [fPage,   setFPage]   = useState(1);

  const [sSearch, setSSearch] = useState('');
  const [sCourse, setSCourse] = useState('');
  const [sDept,   setSDept]   = useState('');
  const [sPage,   setSPage]   = useState(1);

  const [lStatus,  setLStatus]  = useState('');
  const [lPage,    setLPage]    = useState(1);
  const [payMonth, setPayMonth] = useState(new Date().getMonth() + 1);
  const [payYear,  setPayYear]  = useState(new Date().getFullYear());
  const [payPage,  setPayPage]  = useState(1);
  const [gStatus,  setGStatus]  = useState('');

  const PS    = 20;
  const timer = useRef(null);
  const L = k => setLoading(p => ({ ...p, [k]: true  }));
  const U = k => setLoading(p => ({ ...p, [k]: false }));

  const getAll  = async (url, params = {}) => { const r = await api.get(url, { params }); return toArr(r.data); };
  const getPaged = async (url, params = {}) => {
    const r = await api.get(url, { params });
    return { items: toArr(r.data), total: Array.isArray(r.data) ? r.data.length : (r.data?.count || 0) };
  };

  const loadOverview = useCallback(async () => {
    L('ov');
    try {
      const [aR, finR, deptR, facR, stuR, gR, lvR] = await Promise.allSettled([
        api.get('/auth/admin/stats/').then(r => r.data),
        api.get('/auth/finance/dashboard/stats/').then(r => r.data),
        api.get('/auth/departments/').then(r => toArr(r.data)),
        api.get('/auth/admin/users/', { params: { role:'FACULTY', status:'APPROVED', page_size:2000 } }).then(r => toArr(r.data)),
        api.get('/auth/admin/users/', { params: { role:'STUDENT', status:'APPROVED', page_size:2000 } }).then(r => toArr(r.data)),
        api.get('/auth/grievances/officer/stats/').then(r => r.data),
        api.get('/auth/leave/stats/').then(r => r.data),
      ]);

      const adminS = aR.status   === 'fulfilled' ? aR.value   : {};
      const finS   = finR.status === 'fulfilled' ? finR.value : {};
      const deptL  = deptR.status === 'fulfilled' ? deptR.value : [];
      const facL   = facR.status  === 'fulfilled' ? facR.value  : [];
      const stuL   = stuR.status  === 'fulfilled' ? stuR.value  : [];
      const gS     = gR.status    === 'fulfilled' ? gR.value    : null;
      const lvS    = lvR.status   === 'fulfilled' ? lvR.value   : null;

      setAllFaculty(facL);
      setAllStudents(stuL);
      setGrievStats(gS);
      setLeaveStats(lvS);
      setFinStats(finS);

      const enriched = deptL.map(d => {
        const dFac = facL.filter(u => u.profile?.faculty_department === d.id);
        const dStu = stuL.filter(u => u.profile?.student_department  === d.id);
        const hod  = dFac.find(u => ['HEAD_OF_DEPARTMENT','PROFESSOR_HOD'].includes(u.profile?.faculty_subrole));
        return {
          ...d,
          faculty_count: dFac.length,
          student_count: dStu.length,
          hod_name: hod ? `${hod.first_name || ''} ${hod.last_name || ''}`.trim() || hod.username : null,
        };
      });
      setDepts(enriched);

      setStats({
        total_students:          adminS.total_students,
        total_faculty:           adminS.total_faculty,
        pending_admissions:      adminS.pending_admissions,
        total_staff:             finS.total_staff,
        total_revenue_collected: finS.total_revenue_collected,
        total_pending_fees:      finS.total_pending_fees,
        monthly_salary_expense:  finS.monthly_salary_expense,
        pending_leaves:          lvS?.total_pending  || 0,
        escalated_to_dean:       gS?.escalated       || 0,
        opd_tokens_today: 0,
      });

      api.get('/auth/reception/tokens/').then(r => {
        const t    = toArr(r.data);
        const done = t.filter(x => x.status === 'COMPLETED').length;
        setOpd({ total_tokens:t.length, completed:done, in_progress:t.filter(x=>x.status==='IN_PROGRESS').length, completion_rate: t.length > 0 ? Math.round(done/t.length*100) : 0 });
        setStats(s => ({ ...s, opd_tokens_today: t.length }));
      }).catch(() => {});
    } catch(e) { console.error('loadOverview', e); }
    finally { U('ov'); }
  }, []);

  const loadDepts = useCallback(async () => {
    if (depts.length > 0) return;
    L('dt');
    try {
      const [dL, fL, sL] = await Promise.all([
        getAll('/auth/departments/'),
        getAll('/auth/admin/users/', { role:'FACULTY', status:'APPROVED', page_size:2000 }),
        getAll('/auth/admin/users/', { role:'STUDENT', status:'APPROVED', page_size:2000 }),
      ]);
      if (fL.length) setAllFaculty(fL);
      if (sL.length) setAllStudents(sL);
      setDepts(dL.map(d => {
        const dF = fL.filter(u => u.profile?.faculty_department === d.id);
        const dS = sL.filter(u => u.profile?.student_department  === d.id);
        const hod = dF.find(u => ['HEAD_OF_DEPARTMENT','PROFESSOR_HOD'].includes(u.profile?.faculty_subrole));
        return { ...d, faculty_count:dF.length, student_count:dS.length,
          hod_name: hod ? `${hod.first_name||''} ${hod.last_name||''}`.trim() || hod.username : null };
      }));
    } catch(e) { console.error(e); }
    finally { U('dt'); }
  }, [depts.length]);

  const loadUsers = useCallback(async () => {
    L('u');
    try {
      const p = { page:uPage, page_size:PS };
      if (uSearch) p.search = uSearch;
      if (uRole)   p.role   = uRole;
      if (uStatus) p.status = uStatus;
      const { items, total } = await getPaged('/auth/admin/users/', p);
      setUsers(items); setUTotal(total);
    } catch(e) { console.error(e); setUsers([]); }
    finally { U('u'); }
  }, [uPage, uSearch, uRole, uStatus]);

  const ensureFaculty = useCallback(async () => {
    if (allFaculty.length > 0) return;
    L('f');
    try { const l = await getAll('/auth/admin/users/', { role:'FACULTY', status:'APPROVED', page_size:2000 }); setAllFaculty(l); }
    catch(e) { console.error(e); }
    finally { U('f'); }
  }, [allFaculty.length]);

  const ensureStudents = useCallback(async () => {
    if (allStudents.length > 0) return;
    L('s');
    try { const l = await getAll('/auth/admin/users/', { role:'STUDENT', status:'APPROVED', page_size:2000 }); setAllStudents(l); }
    catch(e) { console.error(e); }
    finally { U('s'); }
  }, [allStudents.length]);

  const loadLeaves = useCallback(async () => {
    L('l');
    try {
      const p = { page:lPage, page_size:PS };
      if (lStatus) p.status = lStatus;
      const [{ items, total }, lvS] = await Promise.all([
        getPaged('/auth/leave/department/', p).catch(() => getPaged('/auth/leave/my-leaves/', p)),
        api.get('/auth/leave/stats/').then(r => r.data).catch(() => null),
      ]);
      setLeaves(items); setLvTotal(total);
      if (lvS) setLeaveStats(lvS);
    } catch(e) { console.error(e); setLeaves([]); }
    finally { U('l'); }
  }, [lPage, lStatus]);

  const loadPayroll = useCallback(async () => {
    L('p');
    try {
      const [{ items, total }, pS] = await Promise.all([
        getPaged('/auth/payroll/records/', { month:payMonth, year:payYear, page:payPage, page_size:PS }),
        api.get('/auth/payroll/summary/', { params:{ month:payMonth, year:payYear } }).then(r => r.data).catch(() => null),
      ]);
      setPayroll(items); setPyTotal(total); setPaySummary(pS);
    } catch(e) { console.error(e); setPayroll([]); }
    finally { U('p'); }
  }, [payMonth, payYear, payPage]);

  const loadGrievances = useCallback(async () => {
    L('g');
    try {
      const p = {}; if (gStatus) p.status = gStatus;
      const [gL, gS] = await Promise.all([
        getAll('/auth/grievances/', p),
        api.get('/auth/grievances/officer/stats/').then(r => r.data).catch(() => null),
      ]);
      setGrievances(gL); if (gS) setGrievStats(gS);
    } catch(e) { console.error(e); setGrievances([]); }
    finally { U('g'); }
  }, [gStatus]);

  const loadFinance = useCallback(async () => {
    L('fn');
    try {
      const [finR, feeR, salR, labR] = await Promise.allSettled([
        api.get('/auth/finance/dashboard/stats/').then(r => r.data),
        api.get('/auth/finance/fee-structures/').then(r => toArr(r.data)),
        api.get('/auth/finance/salary-structures/').then(r => toArr(r.data)),
        api.get('/auth/lab/billing/stats/').then(r => r.data),
      ]);
      if (finR.status === 'fulfilled') setFinStats(finR.value);
      if (feeR.status === 'fulfilled') setFeeStructs(feeR.value);
      if (salR.status === 'fulfilled') setSalStructs(salR.value);
      if (labR.status === 'fulfilled') setLabStats(labR.value);

      api.get('/auth/finance/users/', { params:{ role:'STUDENT', page_size:2000 } }).then(r => {
        const stu = toArr(r.data);
        const map = {};
        stu.forEach(u => {
          const c     = u.profile?.course || 'UNKNOWN';
          const label = u.profile?.course_display || c;
          if (!map[c]) map[c] = { course:c, label, total:0, collected:0, pending:0 };
          map[c].total++;
          map[c].collected += parseFloat(u.financial_record?.tuition_paid     || 0);
          map[c].pending   += parseFloat(u.financial_record?.tuition_pending  || 0);
        });
        setCourseBreak(Object.values(map));
      }).catch(() => {});
    } catch(e) { console.error('loadFinance', e); }
    finally { U('fn'); }
  }, []);

  useEffect(() => {
    if (section === 'overview')    loadOverview();
    if (section === 'departments') loadDepts();
    if (section === 'users')       loadUsers();
    if (section === 'faculty')     ensureFaculty();
    if (section === 'students')    ensureStudents();
    if (section === 'leaves')      loadLeaves();
    if (section === 'payroll')     loadPayroll();
    if (section === 'grievances')  loadGrievances();
    if (section === 'finance')     loadFinance();
  }, [section]);

  useEffect(() => { clearTimeout(timer.current); timer.current = setTimeout(() => { setUPage(1); loadUsers(); }, 360); }, [uSearch, uRole, uStatus]);
  useEffect(() => { loadUsers(); }, [uPage]);
  useEffect(() => { setLPage(1); loadLeaves(); }, [lStatus]);
  useEffect(() => { loadLeaves(); }, [lPage]);
  useEffect(() => { setPayPage(1); loadPayroll(); }, [payMonth, payYear]);
  useEffect(() => { loadPayroll(); }, [payPage]);
  useEffect(() => { if (section === 'grievances') loadGrievances(); }, [gStatus]);

  const filteredFaculty = allFaculty.filter(u => {
    if (fDept && u.profile?.faculty_department !== parseInt(fDept)) return false;
    if (fSearch) {
      const q = fSearch.toLowerCase();
      return `${u.first_name} ${u.last_name} ${u.email} ${u.username} ${u.profile?.employee_id || ''}`.toLowerCase().includes(q);
    }
    return true;
  });

  const filteredStudents = allStudents.filter(u => {
    if (sDept   && u.profile?.student_department !== parseInt(sDept))   return false;
    if (sCourse && u.profile?.course             !== sCourse)            return false;
    if (sSearch) {
      const q = sSearch.toLowerCase();
      return `${u.first_name} ${u.last_name} ${u.email} ${u.username} ${u.profile?.student_id || ''}`.toLowerCase().includes(q);
    }
    return true;
  });

  const facPageItems = filteredFaculty.slice((fPage - 1) * PS, fPage * PS);
  const stuPageItems = filteredStudents.slice((sPage - 1) * PS, sPage * PS);
  useEffect(() => setFPage(1), [fSearch, fDept]);
  useEffect(() => setSPage(1), [sSearch, sCourse, sDept]);

  const navClick = id => { setSection(id); setSideOpen(false); };
  const goFacByDept = deptId => { setFDept(String(deptId)); setFSearch('');                    setSection('faculty');   setSideOpen(false); };
  const goStuByDept = deptId => { setSDept(String(deptId)); setSSearch(''); setSCourse('');    setSection('students');  setSideOpen(false); };

  const forceRefresh = () => {
    if      (section === 'overview')    { setDepts([]); setAllFaculty([]); setAllStudents([]); setTimeout(loadOverview, 50); }
    else if (section === 'departments') { setDepts([]); setTimeout(loadDepts, 50); }
    else if (section === 'users')       loadUsers();
    else if (section === 'faculty')     { setAllFaculty([]);  setTimeout(ensureFaculty,  50); }
    else if (section === 'students')    { setAllStudents([]); setTimeout(ensureStudents, 50); }
    else if (section === 'leaves')      loadLeaves();
    else if (section === 'payroll')     loadPayroll();
    else if (section === 'grievances')  loadGrievances();
    else if (section === 'finance')     loadFinance();
  };

  const NAV = [
    { id:'overview',    icon:'🏛',  label:'Overview'     },
    { id:'departments', icon:'🏢',  label:'Departments'  },
    { id:'users',       icon:'👥',  label:'All Users'    },
    { id:'faculty',     icon:'👨‍🏫', label:'Faculty'      },
    { id:'students',    icon:'🎓',  label:'Students'     },
    { id:'leaves',      icon:'📋',  label:'Leaves'       },
    { id:'payroll',     icon:'💰',  label:'Payroll'      },
    { id:'grievances',  icon:'⚖️',  label:'Grievances'  },
    { id:'finance',     icon:'📊',  label:'Financials'   },
    { id:'calendar',    icon:'📅',  label:'Calendar'     },
    ...(hasHostelAllocation ? [
      { id:'hostel',    icon:'🏨',  label:'Hostel'       }
    ] : []),
  ];
  const TITLES = {
    overview:'Institutional Overview', departments:'Academic Departments', users:'All Users',
    faculty:'Faculty Directory',       students:'Student Registry',        leaves:'Leave Management',
    payroll:'Payroll Records',         grievances:'Grievance Management',  finance:'Financial Overview',
    calendar: 'Institutional Calendar',
    hostel: 'My Hostel Allocation',
  };
  const MONTHS = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];

  // ─── SECTION RENDERERS ────────────────────────────────────────────────────

  const renderOverview = () => (
    <div>
      <div className="d-sg4">
        <Tile icon="🎓" label="Total Students"     value={stats?.total_students}      color="#3d5ef5" colorBg="rgba(61,94,245,.08)"   onClick={() => setSection('students')} />
        <Tile icon="👨‍🏫" label="Total Faculty"      value={stats?.total_faculty}       color="#1a9e66" colorBg="rgba(26,158,102,.09)"  onClick={() => setSection('faculty')} />
        <Tile icon="🏢" label="Departments"         value={depts.length || '—'}        color="#7b5cf0" colorBg="rgba(123,92,240,.09)"   onClick={() => setSection('departments')} />
        <Tile icon="⏳" label="Pending Admissions"  value={stats?.pending_admissions}  color="#c88a00" colorBg="rgba(200,138,0,.09)" />
      </div>
      <div className="d-sg4">
        <Tile icon="📋" label="Pending Leaves"     value={stats?.pending_leaves}       color="#c96820" colorBg="rgba(201,104,32,.09)"  onClick={() => { setLStatus('PENDING'); setSection('leaves'); }} />
        <Tile icon="⚖️" label="Escalated to Dean"  value={stats?.escalated_to_dean}   color="#d63060" colorBg="rgba(214,48,96,.09)"   sub={stats?.escalated_to_dean > 0 ? 'Needs action' : null} onClick={() => setSection('grievances')} />
        <Tile icon="🏥" label="OPD Today"           value={stats?.opd_tokens_today ?? '—'} color="#0d9eb0" colorBg="rgba(13,158,176,.09)" />
        <Tile icon="💵" label="Revenue Collected"   value={fmtK(stats?.total_revenue_collected)} color="#1a9e66" colorBg="rgba(26,158,102,.09)" onClick={() => setSection('finance')} />
      </div>

      <div className="d-2c" style={{ marginBottom:16 }}>
        <Card>
          <CardHdr title="Department Overview" sub={`${depts.length} departments`} right={<Btn v="ghost" xs onClick={() => setSection('departments')}>View All →</Btn>} />
          {(loading.ov && depts.length === 0) ? <Spin /> : (
            <div style={{ padding:'14px 16px', display:'grid', gridTemplateColumns:'repeat(auto-fill, minmax(178px,1fr))', gap:10 }}>
              {depts.slice(0, 9).map(d => (
                <div key={d.id} style={{ background:'var(--surface2)', borderRadius:10, padding:'12px 14px', border:'1px solid var(--border2)' }}>
                  <div style={{ fontSize:12, fontWeight:700, color:'var(--text)', marginBottom:6, lineHeight:1.3 }}>{d.name}</div>
                  <div style={{ display:'flex', gap:10, marginBottom:4 }}>
                    <span style={{ fontSize:11, color:'#1a9e66', fontWeight:600 }}>👨‍🏫 {d.faculty_count}</span>
                    <span style={{ fontSize:11, color:'#3d5ef5', fontWeight:600 }}>🎓 {d.student_count}</span>
                  </div>
                  {d.hod_name && <div style={{ fontSize:10, color:'var(--text3)', marginBottom:6 }}>HOD: {d.hod_name}</div>}
                  <div style={{ display:'flex', gap:5 }}>
                    <Btn v="ghost" xs onClick={() => goFacByDept(d.id)}>Faculty</Btn>
                    <Btn v="ghost" xs onClick={() => goStuByDept(d.id)}>Students</Btn>
                  </div>
                </div>
              ))}
            </div>
          )}
        </Card>

        <div style={{ display:'flex', flexDirection:'column', gap:14 }}>
          {opd && (
            <Card style={{ padding:'16px 20px' }}>
              <div style={{ fontSize:12, fontWeight:700, color:'var(--text2)', marginBottom:10 }}>🏥 OPD — Today</div>
              <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:10 }}>
                <Fld label="Tokens"      value={opd.total_tokens} />
                <Fld label="Completed"   value={opd.completed} />
                <Fld label="In Progress" value={opd.in_progress} />
                <Fld label="Completion"  value={`${opd.completion_rate}%`} />
              </div>
            </Card>
          )}
          {grievStats && (
            <Card style={{ padding:'16px 20px' }}>
              <div style={{ fontSize:12, fontWeight:700, color:'var(--text2)', marginBottom:10 }}>⚖️ Grievances</div>
              <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:10 }}>
                <Fld label="Total"     value={grievStats.total} />
                <Fld label="Escalated" value={grievStats.escalated} />
                <Fld label="Pending"   value={grievStats.pending} />
                <Fld label="Resolved"  value={grievStats.resolved} />
              </div>
              <Btn v="accent" xs style={{ marginTop:10 }} onClick={() => setSection('grievances')}>Manage →</Btn>
            </Card>
          )}
          {leaveStats && (
            <Card style={{ padding:'16px 20px' }}>
              <div style={{ fontSize:12, fontWeight:700, color:'var(--text2)', marginBottom:10 }}>📋 Leaves</div>
              <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:10 }}>
                <Fld label="Pending"  value={leaveStats.total_pending  || leaveStats.my_pending}  />
                <Fld label="Approved" value={leaveStats.total_approved || leaveStats.my_approved} />
              </div>
            </Card>
          )}
        </div>
      </div>
    </div>
  );

  const renderDepartments = () => (
    (loading.dt && depts.length === 0) ? <Spin /> : depts.length === 0 ? <Empty /> : (
      <div className="d-dg3">
        {depts.map(d => (
          <Card key={d.id} style={{ padding:'20px' }}>
            <div style={{ marginBottom:12 }}>
              <div style={{ fontFamily:'var(--font-head)', fontSize:16, color:'var(--text)', fontWeight:400, lineHeight:1.3, marginBottom:6 }}>{d.name}</div>
              <Tag label={d.code} color="var(--accent)" bg="var(--accent-dim)" />
            </div>
            {d.description && <p style={{ fontSize:12, color:'var(--text3)', lineHeight:1.6, marginBottom:12 }}>{d.description}</p>}
            <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:10, marginBottom:12 }}>
              <div style={{ background:'rgba(26,158,102,.07)', borderRadius:8, padding:'10px 14px', border:'1px solid rgba(26,158,102,.18)' }}>
                <div style={{ fontSize:10, color:'#1a9e66', fontWeight:700, textTransform:'uppercase', letterSpacing:'.08em' }}>Faculty</div>
                <div style={{ fontSize:26, fontWeight:400, color:'#1a9e66', fontFamily:'var(--font-head)' }}>{d.faculty_count}</div>
              </div>
              <div style={{ background:'rgba(61,94,245,.07)', borderRadius:8, padding:'10px 14px', border:'1px solid rgba(61,94,245,.18)' }}>
                <div style={{ fontSize:10, color:'#3d5ef5', fontWeight:700, textTransform:'uppercase', letterSpacing:'.08em' }}>Students</div>
                <div style={{ fontSize:26, fontWeight:400, color:'#3d5ef5', fontFamily:'var(--font-head)' }}>{d.student_count}</div>
              </div>
            </div>
            {d.hod_name && (
              <div style={{ fontSize:12, color:'var(--text2)', padding:'8px 12px', background:'var(--surface2)', borderRadius:8, border:'1px solid var(--border2)', marginBottom:12 }}>
                🎯 HOD: <strong style={{ color:'var(--text)' }}>{d.hod_name}</strong>
              </div>
            )}
            <div style={{ display:'flex', gap:6 }}>
              <Btn v="accent" xs onClick={() => goFacByDept(d.id)}>Faculty ({d.faculty_count})</Btn>
              <Btn v="ghost"  xs onClick={() => goStuByDept(d.id)}>Students ({d.student_count})</Btn>
            </div>
          </Card>
        ))}
      </div>
    )
  );

  const renderUsers = () => (
    <Card>
      <CardHdr title="All Users" sub={`${uTotal.toLocaleString()} records`} right={
        <>
          <Inp value={uSearch} onChange={e => setUSearch(e.target.value)} placeholder="🔍 Search name / email / ID…" style={{ width:220 }} />
          <Sel value={uRole} onChange={e => { setURole(e.target.value); setUPage(1); }}>
            <option value="">All Roles</option>
            {Object.entries(ROLE_META).map(([k,v]) => <option key={k} value={k}>{v.label}</option>)}
          </Sel>
          <Sel value={uStatus} onChange={e => { setUStatus(e.target.value); setUPage(1); }}>
            <option value="">All Status</option>
            <option value="APPROVED">Approved</option>
            <option value="PENDING">Pending</option>
            <option value="REJECTED">Rejected</option>
          </Sel>
        </>
      } />
      <TblWrap loading={loading.u} empty="No users found"
        cols={['User','Role','Status','Email','Phone','ID / Dept / Course','Registered','Actions']}
        rows={users.map(u => {
          const p = u.profile || {};
          const idCol = u.role === 'STUDENT'
            ? `${p.student_id || '—'} · ${p.course_display || p.course || '—'}`
            : `${p.employee_id || '—'} · ${p.faculty_department_name || p.designation || u.role}`;
          return (
            <tr key={u.id}
              onMouseEnter={e => e.currentTarget.style.background = 'var(--surface2)'}
              onMouseLeave={e => e.currentTarget.style.background = ''}>
              <Td>
                <div style={{ display:'flex', alignItems:'center', gap:10 }}>
                  <Av user={u} />
                  <div>
                    <div style={{ fontWeight:600, color:'var(--text)', fontSize:13 }}>{u.first_name || ''} {u.last_name || ''}{!u.first_name && !u.last_name ? u.username : ''}</div>
                    <div style={{ fontSize:11, color:'var(--text3)' }}>@{u.username}</div>
                  </div>
                </div>
              </Td>
              <Td><RoleTag role={u.role} /></Td>
              <Td><StatusTag status={u.approval_status} /></Td>
              <Td style={{ maxWidth:160 }}><div style={{ overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap', fontSize:12 }}>{fv(u.email)}</div></Td>
              <Td style={{ fontSize:12 }}>{fv(u.phone_number)}</Td>
              <Td style={{ fontSize:11, color:'var(--text3)', maxWidth:190 }}><div style={{ overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>{idCol}</div></Td>
              <Td style={{ fontSize:12 }}>{fmtD(u.created_at)}</Td>
              <Td><Btn xs onClick={() => setSelUser(u.id)}>Details</Btn></Td>
            </tr>
          );
        })}
      />
      <Pager page={uPage} total={uTotal} pageSize={PS} setPage={setUPage} />
    </Card>
  );

  const renderFaculty = () => {
    const deptName = fDept ? (depts.find(d => d.id === parseInt(fDept))?.name || `Dept #${fDept}`) : null;
    return (
      <Card>
        <CardHdr
          title={deptName ? `Faculty — ${deptName}` : 'All Faculty'}
          sub={`${filteredFaculty.length} members`}
          right={<>
            <Inp value={fSearch} onChange={e => setFSearch(e.target.value)} placeholder="🔍 Search…" style={{ width:180 }} />
            <Sel value={fDept} onChange={e => setFDept(e.target.value)}>
              <option value="">All Departments</option>
              {depts.map(d => <option key={d.id} value={d.id}>{d.name} ({d.faculty_count})</option>)}
            </Sel>
            {fDept && <Btn v="ghost" xs onClick={() => setFDept('')}>✕ Clear</Btn>}
          </>}
        />
        {(loading.f && allFaculty.length === 0) ? <Spin /> : (
          <>
            <TblWrap loading={false} empty="No faculty found"
              cols={['Faculty','Department','Subrole','Emp ID','Phone','Monthly Salary','Joining Date','Actions']}
              rows={facPageItems.map(u => {
                const p = u.profile || {};
                const fr = u.financial_record || {};
                return (
                  <tr key={u.id}
                    onMouseEnter={e => e.currentTarget.style.background = 'var(--surface2)'}
                    onMouseLeave={e => e.currentTarget.style.background = ''}>
                    <Td>
                      <div style={{ display:'flex', alignItems:'center', gap:10 }}>
                        <Av user={u} />
                        <div>
                          <div style={{ fontWeight:600, color:'var(--text)', fontSize:13 }}>{u.first_name} {u.last_name}</div>
                          <div style={{ fontSize:11, color:'var(--text3)' }}>{fv(u.email)}</div>
                        </div>
                      </div>
                    </Td>
                    <Td style={{ fontSize:12 }}>{fv(p.faculty_department_name)}</Td>
                    <Td>{p.faculty_subrole ? <Tag label={p.faculty_subrole.replace(/_/g,' ')} color="#1a9e66" bg="rgba(26,158,102,.09)" /> : '—'}</Td>
                    <Td style={{ fontFamily:'monospace', fontSize:12 }}>{fv(p.employee_id)}</Td>
                    <Td style={{ fontSize:12 }}>{fv(u.phone_number)}</Td>
                    <Td style={{ color:'#1a9e66', fontWeight:600 }}>{fmtC(fr.monthly_salary)}</Td>
                    <Td style={{ fontSize:12 }}>{fmtD(p.joining_date)}</Td>
                    <Td><Btn xs onClick={() => setSelUser(u.id)}>Details</Btn></Td>
                  </tr>
                );
              })}
            />
            <Pager page={fPage} total={filteredFaculty.length} pageSize={PS} setPage={setFPage} />
          </>
        )}
      </Card>
    );
  };

  const renderStudents = () => {
    const deptName = sDept ? (depts.find(d => d.id === parseInt(sDept))?.name || `Dept #${sDept}`) : null;
    return (
      <Card>
        <CardHdr
          title={deptName ? `Students — ${deptName}` : 'All Students'}
          sub={`${filteredStudents.length} enrolled`}
          right={<>
            <Inp value={sSearch} onChange={e => setSSearch(e.target.value)} placeholder="🔍 Search…" style={{ width:180 }} />
            <Sel value={sCourse} onChange={e => setSCourse(e.target.value)}>
              <option value="">All Courses</option>
              <option value="MBBS">MBBS</option>
              <option value="PG">Post Graduate</option>
              <option value="SUPER_SPECIALITY">Super Speciality</option>
              <option value="INTERNSHIP">Internship</option>
            </Sel>
            <Sel value={sDept} onChange={e => setSDept(e.target.value)}>
              <option value="">All Departments</option>
              {depts.map(d => <option key={d.id} value={d.id}>{d.name} ({d.student_count})</option>)}
            </Sel>
            {(sDept || sCourse) && <Btn v="ghost" xs onClick={() => { setSDept(''); setSCourse(''); }}>✕ Clear</Btn>}
          </>}
        />
        {(loading.s && allStudents.length === 0) ? <Spin /> : (
          <>
            <TblWrap loading={false} empty="No students found"
              cols={['Student','Course','Department','Student ID','Total Fee','Fee Paid','Pending','Overdue','Actions']}
              rows={stuPageItems.map(u => {
                const p = u.profile || {};
                const fr = u.financial_record || {};
                return (
                  <tr key={u.id}
                    onMouseEnter={e => e.currentTarget.style.background = 'var(--surface2)'}
                    onMouseLeave={e => e.currentTarget.style.background = ''}>
                    <Td>
                      <div style={{ display:'flex', alignItems:'center', gap:10 }}>
                        <Av user={u} />
                        <div>
                          <div style={{ fontWeight:600, color:'var(--text)', fontSize:13 }}>{u.first_name} {u.last_name}</div>
                          <div style={{ fontSize:11, color:'var(--text3)' }}>{fv(u.email)}</div>
                        </div>
                      </div>
                    </Td>
                    <Td><Tag label={p.course_display || p.course || '—'} color="#3d5ef5" bg="rgba(61,94,245,.08)" /></Td>
                    <Td style={{ fontSize:12 }}>{fv(p.student_department_name, 'General / MBBS')}</Td>
                    <Td style={{ fontFamily:'monospace', fontSize:12 }}>{fv(p.student_id)}</Td>
                    <Td style={{ fontSize:12 }}>{fmtC(fr.tuition_fee)}</Td>
                    <Td style={{ color:'#1a9e66', fontWeight:600 }}>{fmtC(fr.tuition_paid)}</Td>
                    <Td style={{ color: parseFloat(fr.tuition_pending || 0) > 0 ? '#c88a00' : 'var(--text3)', fontWeight:600 }}>{fmtC(fr.tuition_pending)}</Td>
                    <Td>{fr.is_overdue ? <Tag label="Overdue" color="#d63060" bg="rgba(214,48,96,.09)" /> : <Tag label="On Track" color="#1a9e66" bg="rgba(26,158,102,.09)" />}</Td>
                    <Td><Btn xs onClick={() => setSelUser(u.id)}>Details</Btn></Td>
                  </tr>
                );
              })}
            />
            <Pager page={sPage} total={filteredStudents.length} pageSize={PS} setPage={setSPage} />
          </>
        )}
      </Card>
    );
  };

  const renderLeaves = () => (
    <div>
      {leaveStats && (
        <div className="d-sg4">
          <Tile icon="⏳" label="Pending"  value={leaveStats.total_pending  || leaveStats.my_pending}  color="#c88a00" colorBg="rgba(200,138,0,.09)"   onClick={() => setLStatus('PENDING')} />
          <Tile icon="✅" label="Approved" value={leaveStats.total_approved || leaveStats.my_approved} color="#1a9e66" colorBg="rgba(26,158,102,.09)"  onClick={() => setLStatus('APPROVED')} />
          <Tile icon="❌" label="Rejected" value={leaveStats.total_rejected || leaveStats.my_rejected} color="#d63060" colorBg="rgba(214,48,96,.09)"   onClick={() => setLStatus('REJECTED')} />
          <Tile icon="📋" label="Total"    value={lvTotal}                                             color="#3d5ef5" colorBg="rgba(61,94,245,.08)"   onClick={() => setLStatus('')} />
        </div>
      )}
      <Card>
        <CardHdr title="Leave Applications" sub={`${lvTotal} records`} right={
          <>
            <Sel value={lStatus} onChange={e => { setLStatus(e.target.value); setLPage(1); }}>
              <option value="">All Status</option>
              <option value="PENDING">Pending</option>
              <option value="APPROVED">Approved</option>
              <option value="REJECTED">Rejected</option>
            </Sel>
            {lStatus && <Btn v="ghost" xs onClick={() => setLStatus('')}>✕</Btn>}
          </>
        } />
        <TblWrap loading={loading.l} empty="No leave records"
          cols={['Applicant','Role','Leave Type','From','To','Days','Status','Approver']}
          rows={leaves.map(l => (
            <tr key={l.id}
              onMouseEnter={e => e.currentTarget.style.background = 'var(--surface2)'}
              onMouseLeave={e => e.currentTarget.style.background = ''}>
              <Td>
                <div style={{ fontWeight:600, color:'var(--text)', fontSize:13 }}>{l.applicant_name}</div>
                <div style={{ fontSize:11, color:'var(--text3)' }}>{l.department_name || '—'}</div>
              </Td>
              <Td><RoleTag role={l.applicant_role} /></Td>
              <Td><Tag label={l.leave_type_display || l.leave_type} color="#3d5ef5" bg="rgba(61,94,245,.08)" /></Td>
              <Td style={{ fontSize:12 }}>{fmtD(l.from_date)}</Td>
              <Td style={{ fontSize:12 }}>{fmtD(l.to_date)}</Td>
              <Td style={{ fontWeight:700, color:'var(--text)' }}>{l.duration_days}</Td>
              <Td>
                <Tag label={l.status}
                  color={{ PENDING:'#c88a00', APPROVED:'#1a9e66', REJECTED:'#d63060' }[l.status] || '#6b7494'}
                  bg={{ PENDING:'rgba(200,138,0,.1)', APPROVED:'rgba(26,158,102,.09)', REJECTED:'rgba(214,48,96,.09)' }[l.status] || 'rgba(107,116,148,.1)'} />
                {l.is_negative_balance && <span style={{ marginLeft:4, fontSize:10, color:'#d63060' }}>⚠</span>}
              </Td>
              <Td style={{ fontSize:12 }}>{l.approver_name || '—'}</Td>
            </tr>
          ))}
        />
        <Pager page={lPage} total={lvTotal} pageSize={PS} setPage={setLPage} />
      </Card>
    </div>
  );

  const renderPayroll = () => (
    <div>
      <div style={{ display:'flex', gap:8, marginBottom:14, alignItems:'center', flexWrap:'wrap' }}>
        <Sel value={payMonth} onChange={e => setPayMonth(Number(e.target.value))}>
          {MONTHS.map((m,i) => <option key={i+1} value={i+1}>{m}</option>)}
        </Sel>
        <Sel value={payYear} onChange={e => setPayYear(Number(e.target.value))}>
          {[2024,2025,2026].map(y => <option key={y} value={y}>{y}</option>)}
        </Sel>
        <span style={{ fontSize:12, color:'var(--text3)' }}>{pyTotal} records</span>
      </div>
      {paySummary && (
        <div className="d-sg4">
          <Tile icon="👷" label="Employees"    value={paySummary.total_employees}          color="#3d5ef5" colorBg="rgba(61,94,245,.08)" />
          <Tile icon="💵" label="Gross Salary" value={fmtK(paySummary.total_gross_salary)} color="#1a9e66" colorBg="rgba(26,158,102,.09)" />
          <Tile icon="📉" label="Deductions"   value={fmtK(paySummary.total_deductions)}   color="#d63060" colorBg="rgba(214,48,96,.09)" />
          <Tile icon="✅" label="Net Payable"  value={fmtK(paySummary.total_net_salary)}   color="#c88a00" colorBg="rgba(200,138,0,.09)" />
        </div>
      )}
      <Card>
        <CardHdr title={`Payroll — ${MONTHS[payMonth-1]} ${payYear}`} />
        <TblWrap loading={loading.p} empty="No payroll records for this period"
          cols={['Employee','Role / Subrole','Base Salary','Unpaid Days','LOP Deduction','Net Salary','Status']}
          rows={payroll.map(r => (
            <tr key={r.id}
              onMouseEnter={e => e.currentTarget.style.background = 'var(--surface2)'}
              onMouseLeave={e => e.currentTarget.style.background = ''}>
              <Td>
                <div style={{ fontWeight:600, color:'var(--text)', fontSize:13 }}>{r.user_name}</div>
                <div style={{ fontSize:11, color:'var(--text3)', fontFamily:'monospace' }}>{r.user_employee_id || '—'}</div>
              </Td>
              <Td>
                <RoleTag role={r.user_role} />
                {r.user_subrole && <div style={{ fontSize:10, color:'var(--text3)', marginTop:3 }}>{r.user_subrole}</div>}
              </Td>
              <Td>{fmtC(r.base_salary)}</Td>
              <Td>{r.unpaid_leave_days > 0 ? <Tag label={`${r.unpaid_leave_days}d`} color="#d63060" bg="rgba(214,48,96,.09)" /> : <span style={{ color:'var(--text3)', fontSize:12 }}>—</span>}</Td>
              <Td style={{ color:'#d63060', fontWeight:600 }}>{parseFloat(r.leave_deduction || 0) > 0 ? `-${fmtC(r.leave_deduction)}` : '—'}</Td>
              <Td style={{ color:'#1a9e66', fontWeight:700, fontSize:14 }}>{fmtC(r.net_salary)}</Td>
              <Td><Tag label={r.status}
                color={{ DRAFT:'#6b7494', CALCULATED:'#3d5ef5', APPROVED:'#1a9e66', PAID:'#c88a00' }[r.status] || '#6b7494'}
                bg={{ DRAFT:'rgba(107,116,148,.1)', CALCULATED:'rgba(61,94,245,.08)', APPROVED:'rgba(26,158,102,.09)', PAID:'rgba(200,138,0,.1)' }[r.status] || 'rgba(107,116,148,.1)'} /></Td>
            </tr>
          ))}
        />
        <Pager page={payPage} total={pyTotal} pageSize={PS} setPage={setPayPage} />
      </Card>
    </div>
  );

  const renderGrievances = () => (
    <div>
      {grievStats && (
        <div className="d-sg4">
          <Tile icon="📊" label="Total"        value={grievStats.total}        color="#3d5ef5" colorBg="rgba(61,94,245,.08)" />
          <Tile icon="⚠️" label="Escalated"    value={grievStats.escalated}    color="#d63060" colorBg="rgba(214,48,96,.09)"  onClick={() => setGStatus('ESCALATED')} />
          <Tile icon="🔍" label="Under Review" value={grievStats.under_review} color="#c96820" colorBg="rgba(201,104,32,.09)" />
          <Tile icon="✅" label="Resolved"     value={grievStats.resolved}     color="#1a9e66" colorBg="rgba(26,158,102,.09)" />
        </div>
      )}
      <Card>
        <CardHdr title="Grievances" sub={`${grievances.length} records`} right={
          <>
            <Sel value={gStatus} onChange={e => setGStatus(e.target.value)}>
              <option value="">All Status</option>
              {['PENDING','ASSIGNED','UNDER_REVIEW','RESOLVED','REJECTED','ESCALATED','CLOSED','REOPENED'].map(s => <option key={s} value={s}>{s}</option>)}
            </Sel>
            {gStatus && <Btn v="ghost" xs onClick={() => setGStatus('')}>✕</Btn>}
          </>
        } />
        <TblWrap loading={loading.g} empty="No grievances found"
          cols={['ID','From','Subject','Category','Priority','Status','Dept','Date','Actions']}
          rows={grievances.map(g => {
            const gs = GS_COLOR[g.status] || GS_COLOR.CLOSED;
            return (
              <tr key={g.id}
                onMouseEnter={e => e.currentTarget.style.background = 'var(--surface2)'}
                onMouseLeave={e => e.currentTarget.style.background = ''}>
                <Td style={{ fontFamily:'monospace', fontSize:11, color:'var(--accent)', fontWeight:600 }}>{g.grievance_id}</Td>
                <Td>
                  <div style={{ fontSize:13, fontWeight:600, color:'var(--text)' }}>{g.submitted_by?.first_name||''} {g.submitted_by?.last_name||g.submitted_by?.username}</div>
                  <div style={{ fontSize:11, color:'var(--text3)' }}>@{g.submitted_by?.username}</div>
                </Td>
                <Td style={{ maxWidth:180 }}><div style={{ overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>{g.subject}</div></Td>
                <Td><Tag label={g.category_display || g.category} color="#3d5ef5" bg="rgba(61,94,245,.08)" /></Td>
                <Td><Tag label={g.priority} color={g.priority==='HIGH'?'#d63060':g.priority==='MEDIUM'?'#c88a00':'#6b7494'} bg={g.priority==='HIGH'?'rgba(214,48,96,.09)':g.priority==='MEDIUM'?'rgba(200,138,0,.1)':'rgba(107,116,148,.1)'} /></Td>
                <Td><Tag label={g.status} color={gs.c} bg={gs.bg} /></Td>
                <Td style={{ fontSize:12 }}>{g.department_assigned || '—'}</Td>
                <Td style={{ fontSize:12 }}>{fmtD(g.created_at)}</Td>
                <Td><Btn xs v={g.status==='ESCALATED'?'primary':'ghost'} onClick={() => setSelGriev(g.id)}>{g.status==='ESCALATED'?'Action':'View'}</Btn></Td>
              </tr>
            );
          })}
        />
      </Card>
    </div>
  );

  const renderFinance = () => {
    if (loading.fn && !finStats) return <Spin />;
    const F = finStats || {};
    const netBalance = parseFloat(F.total_revenue_collected || 0) + parseFloat(labStats?.total_revenue || 0) - parseFloat(F.monthly_salary_expense || 0);
    return (
      <div>
        <div className="d-sg3">
          <Tile icon="💵" label="Revenue Collected"  value={fmtK(F.total_revenue_collected)} color="#1a9e66" colorBg="rgba(26,158,102,.09)" />
          <Tile icon="⌛" label="Pending Fees"        value={fmtK(F.total_pending_fees)}      color="#c88a00" colorBg="rgba(200,138,0,.09)" />
          <Tile icon="⚖️" label="Estimated Balance"  value={fmtK(netBalance)}                color="#3d5ef5" colorBg="rgba(61,94,245,.08)" />
        </div>
        <div className="d-sg3">
          <Tile icon="💼" label="Monthly Salary Bill" value={fmtK(F.monthly_salary_expense)}  color="#c96820" colorBg="rgba(201,104,32,.09)" />
          <Tile icon="🔬" label="Lab Revenue"         value={fmtK(labStats?.total_revenue)}   color="#0d9eb0" colorBg="rgba(13,158,176,.09)" />
          <Tile icon="⚠" label="Overdue Students"     value={F.total_overdue_count}           color="#d63060" colorBg="rgba(214,48,96,.09)" />
        </div>

        <div className="d-2c" style={{ marginBottom:14 }}>
          <Card>
            <CardHdr title="Fee Collection Summary" />
            <div style={{ padding:'16px 18px' }}>
              <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fill, minmax(170px,1fr))', gap:10 }}>
                <Fld label="Total Revenue"      value={fmtC(F.total_revenue_collected)} highlight />
                <Fld label="Total Pending"      value={fmtC(F.total_pending_fees)} />
                <Fld label="Overdue Students"   value={F.total_overdue_count} />
                <Fld label="Transactions Today" value={F.transactions_today} />
                <Fld label="Amount Today"       value={fmtC(F.amount_collected_today)} />
                <Fld label="Total Staff"        value={F.total_staff} />
              </div>
            </div>
          </Card>

          <Card>
            <CardHdr title="Course-wise Collection" sub={courseBreak.length > 0 ? `${courseBreak.length} courses` : 'Loading…'} />
            {courseBreak.length === 0
              ? <div style={{ padding:20, color:'var(--text3)', fontSize:13, textAlign:'center' }}>No student fee data available.</div>
              : (
                <div className="d-tw">
                  <table style={{ width:'100%', borderCollapse:'collapse' }}>
                    <THead cols={['Course','Students','Collected','Pending']} />
                    <tbody>
                      {courseBreak.map((r,i) => (
                        <tr key={i}
                          onMouseEnter={e => e.currentTarget.style.background = 'var(--surface2)'}
                          onMouseLeave={e => e.currentTarget.style.background = ''}>
                          <Td style={{ fontWeight:600 }}>{r.label || r.course}</Td>
                          <Td>{r.total}</Td>
                          <Td style={{ color:'#1a9e66', fontWeight:600 }}>{fmtC(r.collected)}</Td>
                          <Td style={{ color:'#c88a00', fontWeight:600 }}>{fmtC(r.pending)}</Td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
          </Card>
        </div>

        {feeStructs.length > 0 && (
          <Card style={{ marginBottom:14 }}>
            <CardHdr title="Course Fee Structures" sub="Annual tuition fee per course" />
            <div style={{ padding:'14px 16px', display:'grid', gridTemplateColumns:'repeat(auto-fill, minmax(200px,1fr))', gap:10 }}>
              {feeStructs.map(f => (
                <div key={f.id} style={{ background:'var(--surface2)', borderRadius:8, padding:'12px 14px', border:'1px solid var(--border2)' }}>
                  <div style={{ fontSize:13, fontWeight:700, color:'var(--text)', marginBottom:6 }}>{f.course_display || f.course}</div>
                  <div style={{ fontSize:20, fontWeight:400, fontFamily:'var(--font-head)', color:'#1a9e66' }}>{fmtC(f.tuition_fee)}</div>
                  <div style={{ fontSize:11, color:'var(--text3)', marginTop:3 }}>Deadline: {f.default_deadline_days}d from enrollment</div>
                </div>
              ))}
            </div>
          </Card>
        )}

        {salStructs.length > 0 && (
          <Card style={{ marginBottom:14 }}>
            <CardHdr title="Salary Structures" sub="Monthly salary by role / subrole" />
            <div className="d-tw">
              <table style={{ width:'100%', borderCollapse:'collapse' }}>
                <THead cols={['Role / Subrole','Monthly Salary','Active']} />
                <tbody>
                  {salStructs.map(s => (
                    <tr key={s.id}
                      onMouseEnter={e => e.currentTarget.style.background = 'var(--surface2)'}
                      onMouseLeave={e => e.currentTarget.style.background = ''}>
                      <Td style={{ fontWeight:600 }}>{s.role_display || s.faculty_subrole?.replace(/_/g,' ') || s.role}</Td>
                      <Td style={{ color:'#1a9e66', fontWeight:700 }}>{fmtC(s.monthly_salary)}</Td>
                      <Td><Tag label={s.is_active ? 'Active' : 'Inactive'} color={s.is_active?'#1a9e66':'#d63060'} bg={s.is_active?'rgba(26,158,102,.09)':'rgba(214,48,96,.09)'} /></Td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </Card>
        )}

        {labStats && (
          <Card>
            <CardHdr title="Laboratory Revenue" />
            <div style={{ padding:'16px 18px' }}>
              <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fill, minmax(170px,1fr))', gap:10 }}>
                <Fld label="Total Revenue"   value={fmtC(labStats.total_revenue)}   highlight />
                <Fld label="Total Discounts" value={fmtC(labStats.total_discounts)} />
                <Fld label="Total Orders"    value={labStats.total_orders} />
                <Fld label="Paid Orders"     value={labStats.paid_orders} />
                <Fld label="Pending Billing" value={labStats.pending_billing} />
                <Fld label="Unique Patients" value={labStats.unique_patients} />
              </div>
            </div>
          </Card>
        )}
      </div>
    );
  };

  const SECTIONS = {
    overview:    renderOverview,
    departments: renderDepartments,
    users:       renderUsers,
    faculty:     renderFaculty,
    students:    renderStudents,
    leaves:      renderLeaves,
    payroll:     renderPayroll,
    grievances:  renderGrievances,
    finance:     renderFinance,
    calendar:    () => <div style={{ background: '#fff', borderRadius: 16, border: '1px solid #e2e8f0', padding: 20 }}><Calendar /></div>,
    hostel:      () => <HostelUserPortalTab />,
  };

  // ─────────────────────────────────────────────────────────────────────────────
  // RENDER
  // ─────────────────────────────────────────────────────────────────────────────
  return (
    <div className="d-layout">
      <style>{GLOBAL_CSS}</style>

      {/* Mobile overlay */}
      <div className={`d-ovl${sideOpen ? ' open' : ''}`} onClick={() => setSideOpen(false)} />

      {/* ── Sidebar ─────────────────────────────────────────────────────── */}
      <aside className={`d-sidebar${sideOpen ? ' open' : ''}`}>
        {/* Brand */}
        <div style={{ padding:'22px 20px 18px', borderBottom:'1px solid var(--border)', background:'var(--surface2)' }}>
          <div style={{ fontSize:9, fontWeight:700, letterSpacing:'.18em', textTransform:'uppercase', color:'var(--text3)', marginBottom:5 }}>Medical College ERP</div>
          <div style={{ fontFamily:'var(--font-head)', fontSize:22, fontWeight:400, color:'var(--accent)', lineHeight:1.1 }}>Dean<br /><span style={{ color:'var(--text)', fontSize:15, fontStyle:'italic' }}>Portal</span></div>
          <div style={{ width:30, height:2, background:'linear-gradient(90deg,var(--accent),transparent)', borderRadius:2, marginTop:12 }} />
          <div style={{ fontSize:10, color:'var(--text3)', marginTop:8 }}>
            {new Date().toLocaleDateString('en-IN', { weekday:'short', day:'2-digit', month:'long', year:'numeric' })}
          </div>
        </div>

        {/* Nav */}
        <nav style={{ flex:1, padding:'10px 8px', overflowY:'auto' }}>
          <div style={{ fontSize:9, fontWeight:700, letterSpacing:'.14em', textTransform:'uppercase', color:'var(--text3)', padding:'8px 10px 4px' }}>Navigation</div>
          {NAV.map(n => {
            const active = section === n.id;
            const badge  = (n.id==='grievances' && stats?.escalated_to_dean > 0) ? stats.escalated_to_dean
                         : (n.id==='leaves'     && stats?.pending_leaves > 0)    ? stats.pending_leaves : null;
            return (
              <div key={n.id} className={active ? '' : 'd-nav-item'} onClick={() => navClick(n.id)} style={{
                display:'flex', alignItems:'center', gap:10, padding:'9px 10px', borderRadius:8,
                cursor:'pointer', marginBottom:2, fontSize:13,
                fontWeight: active ? 600 : 400,
                color:      active ? 'var(--accent)' : 'var(--text2)',
                background: active ? 'var(--accent-dim)' : 'transparent',
                borderLeft: active ? '3px solid var(--accent)' : '3px solid transparent',
                transition: 'all .15s',
              }}>
                <span style={{ fontSize:16, flexShrink:0 }}>{n.icon}</span>
                <span style={{ flex:1, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>{n.label}</span>
                {badge && (
                  <span style={{ background: n.id==='grievances'?'#d63060':'#c88a00', color:'#fff', fontSize:9, fontWeight:700, borderRadius:20, padding:'1px 5px', minWidth:16, textAlign:'center' }}>{badge}</span>
                )}
              </div>
            );
          })}
        </nav>

        {/* Dean tag */}
        <div style={{ padding:'14px 16px', borderTop:'1px solid var(--border)', background:'var(--surface2)' }}>
          <div style={{ display:'flex', alignItems:'center', gap:10 }}>
            <div style={{ width:38, height:38, borderRadius:'50%', background:'var(--accent-dim)', border:'2px solid rgba(61,94,245,.3)', display:'flex', alignItems:'center', justifyContent:'center', fontSize:16, color:'var(--accent)', flexShrink:0, animation:'glow 3s ease infinite' }}>D</div>
            <div>
              <div style={{ fontSize:13, fontWeight:600, color:'var(--text)' }}>Dean</div>
              <div style={{ fontSize:10, color:'var(--text3)' }}>Academic Head</div>
            </div>
          </div>
        </div>
      </aside>

      {/* ── Main ──────────────────────────────────────────────────────────── */}
      <div className="d-main">
        {/* Topbar */}
        <header className="d-topbar">
          <div style={{ display:'flex', alignItems:'center', gap:14 }}>
            <button className="d-hbg" onClick={() => setSideOpen(o => !o)} aria-label="Toggle menu">
              <svg width="20" height="20" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
                <line x1="3" y1="5"  x2="17" y2="5"  />
                <line x1="3" y1="10" x2="17" y2="10" />
                <line x1="3" y1="15" x2="17" y2="15" />
              </svg>
            </button>
            <div>
              <h1 style={{ fontFamily:'var(--font-head)', fontSize:18, fontWeight:400, color:'var(--text)', letterSpacing:'-.01em' }}>{TITLES[section]}</h1>
              <div style={{ fontSize:11, color:'var(--text3)' }}>Dean's Dashboard</div>
            </div>
          </div>
          <div style={{ display:'flex', gap:8, alignItems:'center' }}>
            {stats?.escalated_to_dean > 0 && section !== 'grievances' && (
              <div onClick={() => setSection('grievances')} style={{ background:'rgba(214,48,96,.08)', border:'1px solid rgba(214,48,96,.2)', borderRadius:8, padding:'6px 12px', cursor:'pointer' }}>
                <span style={{ fontSize:11, color:'#d63060', fontWeight:700 }}>⚖️ {stats.escalated_to_dean} escalated</span>
              </div>
            )}
          </div>
        </header>

        {/* Content */}
        <main className="d-content">
          <div className="d-anim" key={section}>
            {(SECTIONS[section] || renderOverview)()}
          </div>
        </main>
      </div>

      {/* ── Modals ────────────────────────────────────────────────────────── */}
      {selUser  && <UserModal      userId={selUser}  onClose={() => setSelUser(null)} />}
      {selGriev && <GrievanceModal id={selGriev}    onClose={() => setSelGriev(null)} onUpdate={loadGrievances} />}
    </div>
  );
}